import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { useTheme } from "../components/ThemeProvider";
import { useLanguage } from "../components/LanguageProvider";
import { Logo, LogoIcon } from "../components/Logo";
import { cn } from "../lib/utils";
import jsPDF from "jspdf";

const colorTokens = [
  { name: "Primary", var: "--primary", desc: "Main brand color" },
  { name: "Primary Glow", var: "--primary-glow", desc: "Lighter primary variant" },
  { name: "Secondary", var: "--secondary", desc: "Secondary actions" },
  { name: "Accent", var: "--accent", desc: "Highlights & accents" },
  { name: "Muted", var: "--muted", desc: "Backgrounds & subtle elements" },
  { name: "Destructive", var: "--destructive", desc: "Errors & dangerous actions" },
  { name: "Success", var: "--success", desc: "Success states" },
  { name: "Warning", var: "--warning", desc: "Warning states" },
  { name: "Info", var: "--info", desc: "Informational states" },
];

const chartColors = [
  { name: "Chart 1", var: "--chart-1" },
  { name: "Chart 2", var: "--chart-2" },
  { name: "Chart 3", var: "--chart-3" },
  { name: "Chart 4", var: "--chart-4" },
  { name: "Chart 5", var: "--chart-5" },
];

export default function DesignSystem() {
  const { theme, setTheme } = useTheme();
  const { t } = useLanguage();
  const [sliderValue, setSliderValue] = useState([50]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleExportPDF = () => {
    const pdf = new jsPDF('portrait');
    
    pdf.setFontSize(24);
    pdf.text('DataForge Design System', 20, 20);
    
    pdf.setFontSize(12);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
    pdf.text(`Theme: ${theme}`, 20, 40);
    
    pdf.setFontSize(16);
    pdf.text('Color Palette', 20, 60);
    
    pdf.setFontSize(10);
    let yPos = 70;
    colorTokens.forEach((token, idx) => {
      pdf.text(`${token.name}: ${token.var}`, 25, yPos);
      yPos += 7;
    });
    
    pdf.setFontSize(16);
    pdf.text('Typography', 20, yPos + 10);
    
    pdf.setFontSize(10);
    yPos += 20;
    pdf.text('H1: Inter Bold 36px', 25, yPos);
    pdf.text('H2: Inter Semibold 30px', 25, yPos + 7);
    pdf.text('H3: Inter Semibold 24px', 25, yPos + 14);
    pdf.text('Body: Inter Regular 16px', 25, yPos + 21);
    pdf.text('Code: JetBrains Mono 14px', 25, yPos + 28);
    
    pdf.setFontSize(16);
    pdf.text('Components', 20, yPos + 45);
    
    pdf.setFontSize(10);
    yPos += 55;
    const components = [
      'Button (Primary, Secondary, Outline, Ghost, Destructive)',
      'Input, Textarea, Select',
      'Checkbox, Switch, Slider',
      'Badge, Alert, Progress',
      'Card, Dialog, Dropdown',
      'Table, Tabs, Tooltip',
      'Avatar, Separator, ScrollArea'
    ];
    components.forEach((comp) => {
      pdf.text(`• ${comp}`, 25, yPos);
      yPos += 7;
    });
    
    pdf.save('dataforge-design-system.pdf');
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-6 lg:space-y-8" id="design-system-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">{t("designSystemTitle")}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">{t("uiComponentLibrary")}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-xs sm:text-sm">{t("theme")}:</Label>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="w-24 sm:w-28 h-8 sm:h-9 text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">{t("light")}</SelectItem>
                <SelectItem value="dark">{t("dark")}</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleExportPDF} className="h-8 sm:h-9 text-xs sm:text-sm">
            <i className="fa-solid fa-file-pdf mr-2"></i>
            {t("exportPDF")}
          </Button>
        </div>
      </div>

      {/* Logo Section */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("brandLogo")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("logoVariations")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="flex flex-col items-center gap-3 p-3 sm:p-4 rounded-lg bg-sidebar-bg">
              <Logo />
              <span className="text-[10px] sm:text-xs text-sidebar-foreground">{t("fullLogo")}</span>
            </div>
            <div className="flex flex-col items-center gap-3 p-3 sm:p-4 rounded-lg bg-sidebar-bg">
              <Logo collapsed />
              <span className="text-[10px] sm:text-xs text-sidebar-foreground">{t("iconOnly")}</span>
            </div>
            <div className="flex flex-col items-center gap-3 p-3 sm:p-4 rounded-lg bg-muted">
              <LogoIcon size="lg" />
              <span className="text-[10px] sm:text-xs text-muted-foreground">{t("largeIcon")}</span>
            </div>
            <div className="flex flex-col items-center gap-3 p-3 sm:p-4 rounded-lg bg-muted">
              <div className="flex gap-2">
                <LogoIcon size="sm" />
                <LogoIcon size="md" />
                <LogoIcon size="lg" />
              </div>
              <span className="text-[10px] sm:text-xs text-muted-foreground">{t("sizeVariants")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Colors */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("colorPalette")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("semanticColors")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
          <div>
            <h4 className="text-xs sm:text-sm font-medium mb-3 sm:mb-4">Semantic Colors</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {colorTokens.map((token) => (
                <div key={token.var} className="space-y-1 sm:space-y-2">
                  <div className="h-12 sm:h-16 rounded-lg border" style={{ backgroundColor: `hsl(var(${token.var}))` }}></div>
                  <p className="text-xs sm:text-sm font-medium">{token.name}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground font-mono">{token.var}</p>
                </div>
              ))}
            </div>
          </div>
          <Separator />
          <div>
            <h4 className="text-xs sm:text-sm font-medium mb-3 sm:mb-4">{t("chartColors")}</h4>
            <div className="flex gap-3 sm:gap-4 flex-wrap">
              {chartColors.map((token) => (
                <div key={token.var} className="flex items-center gap-2">
                  <div className="h-6 w-6 sm:h-8 sm:w-8 rounded-full" style={{ backgroundColor: `hsl(var(${token.var}))` }}></div>
                  <span className="text-[10px] sm:text-xs font-mono">{token.var}</span>
                </div>
              ))}
            </div>
          </div>
          <Separator />
          <div>
            <h4 className="text-xs sm:text-sm font-medium mb-3 sm:mb-4">{t("backgroundForeground")}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="p-3 sm:p-4 rounded-lg bg-background border"><p className="text-[10px] sm:text-xs">Background</p></div>
              <div className="p-3 sm:p-4 rounded-lg bg-card border"><p className="text-[10px] sm:text-xs">Card</p></div>
              <div className="p-3 sm:p-4 rounded-lg bg-muted"><p className="text-[10px] sm:text-xs">Muted</p></div>
              <div className="p-3 sm:p-4 rounded-lg bg-accent"><p className="text-[10px] sm:text-xs">Accent</p></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Typography */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("typography")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("fontFamilies")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-3 sm:space-y-4">
          <div><h1 className="text-2xl sm:text-4xl font-bold">Heading 1 - Inter Bold</h1></div>
          <div><h2 className="text-xl sm:text-3xl font-semibold">Heading 2 - Inter Semibold</h2></div>
          <div><h3 className="text-lg sm:text-2xl font-semibold">Heading 3 - Inter Semibold</h3></div>
          <div><h4 className="text-base sm:text-xl font-medium">Heading 4 - Inter Medium</h4></div>
          <div><p className="text-sm sm:text-base">Body Text - Inter Regular 16px</p></div>
          <div><p className="text-xs sm:text-sm text-muted-foreground">Small Text - Inter Regular 14px</p></div>
          <div><code className="font-mono text-xs sm:text-sm bg-muted px-2 py-1 rounded">Code - JetBrains Mono</code></div>
        </CardContent>
      </Card>

      {/* Buttons */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("buttons")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("buttonVariants")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Button className="h-8 sm:h-9 text-xs sm:text-sm">{t("primary")}</Button>
            <Button variant="secondary" className="h-8 sm:h-9 text-xs sm:text-sm">{t("secondary")}</Button>
            <Button variant="outline" className="h-8 sm:h-9 text-xs sm:text-sm">{t("outline")}</Button>
            <Button variant="ghost" className="h-8 sm:h-9 text-xs sm:text-sm">{t("ghost")}</Button>
            <Button variant="destructive" className="h-8 sm:h-9 text-xs sm:text-sm">{t("destructive")}</Button>
            <Button variant="link" className="h-8 sm:h-9 text-xs sm:text-sm">{t("link")}</Button>
          </div>
          <Separator />
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Button size="sm">{t("small")}</Button>
            <Button size="default">{t("default")}</Button>
            <Button size="lg">{t("large")}</Button>
            <Button size="icon"><i className="fa-solid fa-plus"></i></Button>
          </div>
          <Separator />
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Button className="h-8 sm:h-9 text-xs sm:text-sm"><i className="fa-solid fa-download mr-2"></i>{t("withIcon")}</Button>
            <Button disabled className="h-8 sm:h-9 text-xs sm:text-sm">{t("disabled")}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Form Controls */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("formControls")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("inputFields")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">{t("textInput")}</Label>
              <Input placeholder={t("enterText")} className="h-8 sm:h-9 text-xs sm:text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">{t("searchInput")}</Label>
              <div className="relative">
                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
                <Input placeholder={`${t("search")}...`} className="pl-10 h-8 sm:h-9 text-xs sm:text-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">{t("select")}</Label>
              <Select>
                <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm"><SelectValue placeholder={t("selectOption")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t("option")} 1</SelectItem>
                  <SelectItem value="2">{t("option")} 2</SelectItem>
                  <SelectItem value="3">{t("option")} 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">{t("textarea")}</Label>
              <Textarea placeholder={t("enterDescription")} className="text-xs sm:text-sm" />
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox id="check" />
              <Label htmlFor="check" className="text-xs sm:text-sm">{t("checkbox")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="switch" />
              <Label htmlFor="switch" className="text-xs sm:text-sm">{t("switch")}</Label>
            </div>
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">{t("slider")}: {sliderValue}</Label>
              <Slider value={sliderValue} onValueChange={setSliderValue} max={100} step={1} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badges & Status */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("badgesStatus")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("visualIndicators")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Badge className="text-xs">{t("default")}</Badge>
            <Badge variant="secondary" className="text-xs">{t("secondary")}</Badge>
            <Badge variant="outline" className="text-xs">{t("outline")}</Badge>
            <Badge variant="destructive" className="text-xs">{t("destructive")}</Badge>
          </div>
          <Separator />
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-xs">{t("success")}</Badge>
            <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-xs">{t("warning")}</Badge>
            <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30 text-xs">{t("error")}</Badge>
            <Badge variant="outline" className="bg-info/10 text-info border-info/30 text-xs">{t("info")}</Badge>
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-xs"><span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-success animate-pulse"></span>{t("live")}</Badge>
          </div>
          <Separator />
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center gap-2"><div className="h-2 w-2 rounded-full bg-success"></div><span className="text-xs sm:text-sm">{t("activeOnline")}</span></div>
            <div className="flex items-center gap-2"><div className="h-2 w-2 rounded-full bg-warning"></div><span className="text-xs sm:text-sm">{t("warningPending")}</span></div>
            <div className="flex items-center gap-2"><div className="h-2 w-2 rounded-full bg-destructive"></div><span className="text-xs sm:text-sm">{t("errorOffline")}</span></div>
            <div className="flex items-center gap-2"><div className="h-2 w-2 rounded-full bg-muted-foreground"></div><span className="text-xs sm:text-sm">{t("inactiveDisabled")}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("alertsNotifications")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("contextualFeedback")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-3 sm:space-y-4">
          <Alert><i className="fa-solid fa-circle-info mr-2 text-info text-sm"></i><AlertTitle className="text-sm">{t("information")}</AlertTitle><AlertDescription className="text-xs">{t("informationalAlert")}</AlertDescription></Alert>
          <Alert className="border-success/50 bg-success/10"><i className="fa-solid fa-circle-check mr-2 text-success text-sm"></i><AlertTitle className="text-sm">{t("success")}</AlertTitle><AlertDescription className="text-xs">{t("operationCompleted")}</AlertDescription></Alert>
          <Alert className="border-warning/50 bg-warning/10"><i className="fa-solid fa-triangle-exclamation mr-2 text-warning text-sm"></i><AlertTitle className="text-sm">{t("warning")}</AlertTitle><AlertDescription className="text-xs">{t("pleaseReview")}</AlertDescription></Alert>
          <Alert variant="destructive"><i className="fa-solid fa-circle-exclamation mr-2 text-sm"></i><AlertTitle className="text-sm">{t("error")}</AlertTitle><AlertDescription className="text-xs">{t("somethingWrong")}</AlertDescription></Alert>
        </CardContent>
      </Card>

      {/* Dialogs & Dropdowns */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">Dialogs & Dropdowns</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Modal dialogs and dropdown menus</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
          <div className="flex flex-wrap gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-8 sm:h-9 text-xs sm:text-sm">Open Dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Dialog Title</DialogTitle>
                  <DialogDescription>This is a dialog description with some helpful information.</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm">Dialog content goes here.</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
                  <Button onClick={() => setDialogOpen(false)}>{t("save")}</Button>
                </div>
              </DialogContent>
            </Dialog>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-8 sm:h-9 text-xs sm:text-sm">
                  Dropdown Menu <i className="fa-solid fa-chevron-down ml-2 text-xs"></i>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem><i className="fa-solid fa-eye mr-2"></i>{t("view")}</DropdownMenuItem>
                <DropdownMenuItem><i className="fa-solid fa-pen mr-2"></i>{t("edit")}</DropdownMenuItem>
                <DropdownMenuItem><i className="fa-solid fa-copy mr-2"></i>Copy</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive"><i className="fa-solid fa-trash mr-2"></i>{t("delete")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" className="h-8 sm:h-9 text-xs sm:text-sm">Hover for Tooltip</Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This is a tooltip!</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Tables */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">Tables</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Data tables with various styles</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Name</TableHead>
                  <TableHead className="text-xs sm:text-sm">{t("type")}</TableHead>
                  <TableHead className="text-xs sm:text-sm">{t("status")}</TableHead>
                  <TableHead className="text-right text-xs sm:text-sm">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium text-xs sm:text-sm">John Smith</TableCell>
                  <TableCell className="text-xs sm:text-sm">{t("person")}</TableCell>
                  <TableCell><Badge variant="outline" className="bg-success/10 text-success border-success/30 text-xs">{t("active")}</Badge></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm"><i className="fa-solid fa-ellipsis"></i></Button></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium text-xs sm:text-sm">TechCorp Inc.</TableCell>
                  <TableCell className="text-xs sm:text-sm">{t("organization")}</TableCell>
                  <TableCell><Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-xs">{t("warning")}</Badge></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm"><i className="fa-solid fa-ellipsis"></i></Button></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium text-xs sm:text-sm">New York</TableCell>
                  <TableCell className="text-xs sm:text-sm">{t("location")}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{t("default")}</Badge></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm"><i className="fa-solid fa-ellipsis"></i></Button></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">Tabs</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Tabbed navigation components</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <Tabs defaultValue="tab1">
            <TabsList>
              <TabsTrigger value="tab1" className="text-xs sm:text-sm">Tab 1</TabsTrigger>
              <TabsTrigger value="tab2" className="text-xs sm:text-sm">Tab 2</TabsTrigger>
              <TabsTrigger value="tab3" className="text-xs sm:text-sm">Tab 3</TabsTrigger>
            </TabsList>
            <TabsContent value="tab1" className="p-4 border rounded-lg mt-2">
              <p className="text-xs sm:text-sm">Content for Tab 1. This is the first tab panel.</p>
            </TabsContent>
            <TabsContent value="tab2" className="p-4 border rounded-lg mt-2">
              <p className="text-xs sm:text-sm">Content for Tab 2. This is the second tab panel.</p>
            </TabsContent>
            <TabsContent value="tab3" className="p-4 border rounded-lg mt-2">
              <p className="text-xs sm:text-sm">Content for Tab 3. This is the third tab panel.</p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Cards */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("cardsContainers")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("contentContainers")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Card>
              <CardHeader className="pb-2 p-3 sm:p-4"><CardTitle className="text-xs sm:text-base">{t("basicCard")}</CardTitle></CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0"><p className="text-[10px] sm:text-sm text-muted-foreground">{t("simpleCard")}</p></CardContent>
            </Card>
            <Card className="border-primary/50">
              <CardHeader className="pb-2 p-3 sm:p-4"><CardTitle className="text-xs sm:text-base">{t("highlighted")}</CardTitle></CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0"><p className="text-[10px] sm:text-sm text-muted-foreground">{t("cardAccentBorder")}</p></CardContent>
            </Card>
            <Card className="bg-primary/5">
              <CardHeader className="pb-2 p-3 sm:p-4"><CardTitle className="text-xs sm:text-base">{t("tinted")}</CardTitle></CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0"><p className="text-[10px] sm:text-sm text-muted-foreground">{t("cardTintedBg")}</p></CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Progress & Loading */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("progressLoading")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("progressIndicators")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
          <div className="space-y-3 sm:space-y-4">
            <div className="space-y-2"><Label className="text-xs sm:text-sm">{t("defaultProgress")} (45%)</Label><Progress value={45} /></div>
            <div className="space-y-2"><Label className="text-xs sm:text-sm">{t("success")} (100%)</Label><Progress value={100} className="[&>div]:bg-success" /></div>
            <div className="space-y-2"><Label className="text-xs sm:text-sm">{t("warning")} (75%)</Label><Progress value={75} className="[&>div]:bg-warning" /></div>
          </div>
          <Separator />
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2"><i className="fa-solid fa-spinner fa-spin text-primary"></i><span className="text-xs sm:text-sm">{t("loading")}</span></div>
            <div className="flex items-center gap-2"><i className="fa-solid fa-circle-notch fa-spin text-muted-foreground"></i><span className="text-xs sm:text-sm">{t("processing")}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Avatars */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("avatars")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("userAvatars")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
            <Avatar className="h-8 w-8"><AvatarFallback className="text-xs">JD</AvatarFallback></Avatar>
            <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary text-primary-foreground text-sm">AB</AvatarFallback></Avatar>
            <Avatar className="h-12 w-12"><AvatarFallback className="bg-chart-2 text-white">CD</AvatarFallback></Avatar>
            <Avatar className="h-14 w-14"><AvatarImage src="https://github.com/shadcn.png" /><AvatarFallback>CN</AvatarFallback></Avatar>
          </div>
        </CardContent>
      </Card>

      {/* Spacing & Layout */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("spacingScale")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("consistentSpacing")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="flex items-end gap-2 flex-wrap">
            {[1, 2, 3, 4, 6, 8, 12, 16].map((size) => (
              <div key={size} className="flex flex-col items-center gap-1">
                <div className="bg-primary rounded" style={{ width: `${size * 4}px`, height: `${size * 4}px` }}></div>
                <span className="text-[10px] sm:text-xs text-muted-foreground">{size}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Icons */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-sm sm:text-base">{t("icons")}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">{t("fontAwesomeIcons")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2 sm:gap-4">
            {["fa-chart-line", "fa-diagram-project", "fa-circle-nodes", "fa-map-location-dot", "fa-timeline", "fa-magnifying-glass", "fa-table-cells-large", "fa-bell", "fa-gear", "fa-user", "fa-building", "fa-calendar", "fa-link", "fa-download", "fa-plus", "fa-check", "fa-xmark", "fa-arrow-right", "fa-filter", "fa-sun", "fa-moon", "fa-database", "fa-file-lines", "fa-folder"].map((icon) => (
              <div key={icon} className="flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                <i className={cn("fa-solid text-sm sm:text-lg", icon)}></i>
                <span className="text-[8px] sm:text-[10px] text-muted-foreground truncate w-full text-center">{icon.replace("fa-", "")}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center py-6 sm:py-8 border-t border-border">
        <p className="text-xs sm:text-sm text-muted-foreground">{t("version")}</p>
        <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{t("builtWith")}</p>
      </div>
    </div>
  );
}
